import { Button, Callout, Modal } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useTranslation } from 'common/hooks/useTranslation';
import type {
  AuthenticationFactor,
  AuthenticationMethod,
} from 'modules/authentication-factor/models/authenticationFactors';
import { postResetPaymentPassword } from 'modules/cards/api/postResetPaymentPassword';
import { usePostPaymentPasswordProcedure } from 'modules/cards/hooks/usePostPaymentPasswordProcedure';
import { scaProcedureErrors } from 'modules/cards/models/sca';
import { AlternateAuthenticationMethodModal } from 'src/auth/modules/StrongCustomerAuthentication/components/AlternateAuthenticationMethodModal/AlternateAuthenticationMethodModal';

import glassHourGeorges from './glassHourGeorges.svg';
import mobileImage from '../assets/mobile.png';

type Props = {
  authenticationFactors: Array<AuthenticationFactor>;
  isOpen: boolean;
  onClose: () => void;
  password?: string;
};

export const SfsUkPasswordModal = ({
  authenticationFactors,
  isOpen,
  onClose,
  password,
}: Props) => {
  const { t } = useTranslation('global');
  const dispatch = useDispatch();

  const [procedureId, setProcedureId] = useState<string | undefined>(undefined);
  const [alternateMethodProcedureId, setAlternateMethodProcedureId] = useState<
    string | undefined
  >(undefined);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const [
    showAlternateAuthenticationMethodModal,
    setShowAlternateAuthenticationMethodModal,
  ] = useState(false);

  const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] =
    useState<AuthenticationMethod>('sca');

  const { passwordToShow, procedureFailedReason, setProcedureFailedReason } =
    usePostPaymentPasswordProcedure(
      { authenticationMethod: selectedAuthenticationMethod, procedureId },
      () => {
        setIsResettingPassword(false);
        setSelectedAuthenticationMethod('sca');
        setShowAlternateAuthenticationMethodModal(false);
      },
    );

  useEffect(() => {
    const handleSCAResetPassword = async () => {
      try {
        const response = await postResetPaymentPassword(
          {
            authenticationMethod: selectedAuthenticationMethod,
          },
          dispatch,
        );

        if (selectedAuthenticationMethod === 'sca') {
          setProcedureId(response.procedureId);
        } else {
          setAlternateMethodProcedureId(response.procedureId);
        }
      } catch {
        setProcedureFailedReason('initializationError');
      }
    };
    if (isResettingPassword) {
      handleSCAResetPassword();
    }
  }, [isResettingPassword, selectedAuthenticationMethod]);

  const handleResetPassword = async () => {
    setIsResettingPassword(true);
  };

  const handleUseAlternateMethod = () => {
    setShowAlternateAuthenticationMethodModal(true);
  };

  const handleAlternateAuthenticationMethodModalClose =
    async (): Promise<void> => {
      onClose();
    };

  const handleAlternateAuthenticationMethodSelected = async (
    selectedAuthenticationFactor: AuthenticationFactor,
  ): Promise<void> => {
    let authenticationMethod: AuthenticationMethod;
    switch (selectedAuthenticationFactor.type) {
      case 'TOTP':
        authenticationMethod = 'totp';
        break;
      case 'SMS':
      case 'EMAIL':
        authenticationMethod = 'mfa';
        break;
      default:
        authenticationMethod = 'sca';
        break;
    }
    setProcedureId(undefined);
    setSelectedAuthenticationMethod(authenticationMethod);
  };

  const handleScaVerified = async (): Promise<void> => {
    setProcedureId(alternateMethodProcedureId);
  };

  if (isResettingPassword && showAlternateAuthenticationMethodModal) {
    return (
      <AlternateAuthenticationMethodModal
        factors={authenticationFactors}
        flow="standard"
        isOpen
        onAlternateAuthenticationMethodSelected={
          handleAlternateAuthenticationMethodSelected
        }
        onClose={handleAlternateAuthenticationMethodModalClose}
        onScaVerified={handleScaVerified}
        operationId={alternateMethodProcedureId}
      />
    );
  }

  if (isResettingPassword || procedureFailedReason) {
    return (
      <Modal
        illustration={<img src={mobileImage} alt="" />}
        illustrationHeight="400px"
        title={t('cards.otpPasswordModal.titleReset')}
        isOpen={isOpen}
        onClose={onClose}
        actions={[
          <Button
            key="confirm"
            onClick={onClose}
            text={t('cards.otpPasswordModal.cancel')}
            variant="primaryBrand"
          />,
        ]}
      >
        {procedureId && !procedureFailedReason && (
          <p className="-mt-16 mb-16">
            {t('cards.otpPasswordModal.subtitleReset')}
          </p>
        )}
        {procedureFailedReason && (
          <Callout
            className="mb-16"
            title={t(scaProcedureErrors[procedureFailedReason])}
            variant="alert"
          />
        )}
        {authenticationFactors.length >= 1 && (
          <Button
            variant="tertiaryBrand"
            text={t('cards.otpPasswordModal.alternateMethodCta')}
            onClick={handleUseAlternateMethod}
          />
        )}
      </Modal>
    );
  }

  return (
    <Modal
      illustration={<img src={glassHourGeorges} alt="" />}
      illustrationHeight="338px"
      // @ts-expect-error Grapes hacking time because Georges's arm is hanging out
      title={<div className="-mt-24">{t('requests.sfsUkModal.title')}</div>}
      subtitle={t('requests.sfsUkModal.description')}
      isOpen={isOpen}
      onClose={onClose}
      actions={[
        <div key="ctas" className="flex flex-col items-center">
          <Button
            onClick={onClose}
            text={t('requests.sfsUkModal.cta')}
            variant="primaryBrand"
          />
          <Button
            variant="tertiaryNeutral"
            text={t('requests.sfsUkModal.resetPasswordCta')}
            onClick={handleResetPassword}
            className="mt-8"
          />
        </div>,
      ]}
    >
      <div className="flex flex-col items-center">
        <div
          className="rounded-8 bg-background-tertiary-default text-content-primary body-xxl w-fit px-24 py-4"
          data-mask
        >
          {passwordToShow ?? password}
        </div>
      </div>
    </Modal>
  );
};
